import { useState } from 'react';

const ContactMethods = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    prefecture: '',
    item: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // エラーメッセージをクリア
    if (submitStatus === 'error') {
      setSubmitStatus('idle');
      setErrorMessage('');
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      // バリデーション
      if (!formData.name.trim()) {
        setErrorMessage('お名前を入力してください。');
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }
      if (!formData.email.trim()) {
        setErrorMessage('メールアドレスを入力してください。');
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }
      if (!formData.prefecture) {
        setErrorMessage('都道府県を選択してください。');
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }
      if (!formData.item) {
        setErrorMessage('査定品目を選択してください。');
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }

      // フォームデータを作成
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name.trim());
      if (formData.phone.trim()) {
        formBody.append('phone', formData.phone.trim());
      }
      formBody.append('email', formData.email.trim());
      formBody.append('prefecture', formData.prefecture);
      formBody.append('item', formData.item);
      if (formData.message.trim()) {
        formBody.append('message', formData.message.trim());
      }
      formBody.append('timestamp', new Date().toISOString());

      // フォーム送信
      const response = await fetch('https://readdy.ai/api/form/d60shg74klnl5oj67fvg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          prefecture: '',
          item: '',
          message: ''
        });
        
        setTimeout(() => {
          setSubmitStatus('idle');
          setErrorMessage('');
        }, 5000);
      } else {
        const errorText = await response.text().catch(() => '');
        console.error('送信エラー:', response.status, response.statusText, errorText);
        
        if (errorText.includes('spam')) {
          setErrorMessage('送信内容に問題が検出されました。お手数ですが、LINEまたはお電話でお問い合わせください。');
        } else {
          setErrorMessage('送信に失敗しました。お手数ですが、もう一度お試しください。');
        }
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('送信エラー:', error);
      setErrorMessage('送信に失敗しました。インターネット接続をご確認の上、もう一度お試しください。');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const prefectures = [
    '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
    '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
    '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県',
    '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県',
    '奈良県', '和歌山県', '鳥取県', '島根県', '岡山県', '広島県', '山口県',
    '徳島県', '香川県', '愛媛県', '高知県', '福岡県', '佐賀県', '長崎県',
    '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県'
  ];

  const items = [
    '刀', '鎧（甲冑）・兜', 'その他'
  ];

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            お問い合わせ
          </h2>
          <p className="text-lg text-gray-600">
            お気軽にご相談ください。専門スタッフが丁寧に対応いたします。
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* 連絡先情報 */}
          <div className="flex flex-col gap-8 h-full">
            <div className="bg-green-50 p-8 rounded-lg border-2 border-green-200 flex-1 flex flex-col justify-center">
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                  <i className="ri-line-fill text-white text-3xl"></i>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                LINEでのお問い合わせ
              </h3>
              <p className="text-center text-gray-700 mb-6">
                <span className="inline-block bg-yellow-300 px-3 py-1 rounded-full font-bold text-sm mb-2 whitespace-nowrap">
                  おすすめ（返答率100％）
                </span>
                <br />
                お気軽にLINEでお問い合わせください。<br />
                写真を送っていただくとスムーズです。<br />
                <span className="text-green-600 font-bold mt-2 inline-flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  休日・夜間も対応可能
                </span>
              </p>
              <a
                href="https://lin.ee/Bi3KvRO"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-green-500 text-white py-4 px-6 rounded-lg font-bold text-xl text-center hover:bg-green-600 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
              >
                <i className="ri-line-fill text-2xl mr-2"></i>
                LINEで問い合わせ
              </a>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg flex-1 flex flex-col justify-center">
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 bg-samurai-red rounded-full flex items-center justify-center">
                  <i className="ri-phone-fill text-white text-3xl"></i>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                電話でのお問い合わせ
              </h3>
              <p className="text-center text-gray-700 mb-2">
                お電話でもお気軽にお問い合わせください。<br />
                専門スタッフが丁寧に対応いたします。
              </p>
              <p className="text-center text-gray-600 mb-6 flex items-center justify-center gap-2">
                <i className="ri-time-line text-lg"></i>
                <span className="font-bold">受付時間：平日 9:00〜17:00</span>
              </p>
              <a
                href="tel:070-8453-9292"
                className="block w-full bg-samurai-red text-white py-4 px-6 rounded-lg font-bold text-xl text-center hover:bg-samurai-red/90 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill text-2xl mr-2"></i>
                電話で問い合わせ
              </a>
            </div>
          </div>

          {/* お問い合わせフォーム */}
          <div className="bg-gray-50 p-8 rounded-lg h-full">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              査定お申し込みフォーム
            </h3>
            
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-teal-50 border border-teal-200 rounded-lg text-teal-800">
                <div className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-xl"></i>
                  <span className="font-bold">お問い合わせを受け付けました。</span>
                </div>
                <p className="mt-2 text-sm">担当者より折り返しご連絡いたします。</p>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
                <div className="flex items-center gap-2">
                  <i className="ri-error-warning-fill text-xl"></i>
                  <span className="font-bold">{errorMessage || '送信に失敗しました。'}</span>
                </div>
                <p className="mt-2 text-sm">写真を送りたい場合は、LINEでのお問い合わせが便利です。</p>
              </div>
            )}

            <form 
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              {/* ハニーポット（スパムボット対策） */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                style={{ position: 'absolute', left: '-9999px' }}
                aria-hidden="true"
              />

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="山田 太郎"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  電話番号
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="090-1234-5678"
                />
              </div>

              <div>
                <label htmlFor="prefecture" className="block text-sm font-medium text-gray-700 mb-2">
                  都道府県 <span className="text-red-500">*</span>
                </label>
                <select
                  id="prefecture"
                  name="prefecture"
                  value={formData.prefecture}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                >
                  <option value="">選択してください</option>
                  {prefectures.map((pref) => (
                    <option key={pref} value={pref}>{pref}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="item" className="block text-sm font-medium text-gray-700 mb-2">
                  査定品目 <span className="text-red-500">*</span>
                </label>
                <select
                  id="item"
                  name="item"
                  value={formData.item}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                >
                  <option value="">選択してください</option>
                  {items.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  お問い合わせ内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="査定品の詳細や、ご希望の日時などをご記入ください（500文字以内）"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {formData.message.length}/500文字
                </p>
              </div>

              <p className="text-xs text-gray-500 flex items-center gap-1">
                <i className="ri-information-line"></i>
                写真を送りたい場合は、LINEでのお問い合わせをご利用ください。
              </p>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 text-white py-4 px-6 rounded-lg font-bold text-lg hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? '送信中...' : '無料査定を申し込む'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactMethods;
